import { useState } from "react";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { APP_META_TRAVEL_ADS_API_ENDPOINT } from "../../../../constants/api_urls";
import { getUserToken } from "../../../../utils/functions/userToken";

const useSaveAudience = () => {
  const [isLoading, setIsLoading] = useState(false);
  const token = getUserToken();

  const saveAudience = async ({
    effectiveCampaignId,
    selectedObjective,
    ageRange,
    selectedGender,
    bookingWindowRange,
    audienceRules,
    selectedMetaAudience,
    selectedIncludeAudiences = [],
    selectedExcludeAudiences = [],
    setDrawerOpen,
    onClose
  }) => {
    try {
      setIsLoading(true);
      setDrawerOpen(true);

      const isShared = selectedObjective === "META_AUDIENCE";

      if (isShared && !selectedMetaAudience && selectedIncludeAudiences.length === 0) {
        enqueueSnackbar("Please select at least one audience to include", { variant: "error" });
        setDrawerOpen(false);
        setIsLoading(false);
        return;
      }

      const audienceEntry = isShared
        ? {
            source_type: "SHARED",
            shared_audience_id: selectedMetaAudience
              ? Number(selectedMetaAudience)
              : null,
            shared_audience_ids: selectedIncludeAudiences.length > 0
              ? selectedIncludeAudiences.map(Number)
              : undefined,
            excluded_audience_ids: selectedExcludeAudiences.length > 0
              ? selectedExcludeAudiences.map(Number)
              : undefined,
          }
        : {
            source_type: "NEW",
            shared_audience_id: null,
            audience_type: selectedObjective, // RETARGETING | PROSPECTING
            age_min: Number(ageRange.min),
            age_max: Number(ageRange.max),
            gender: selectedGender,
            booking_window_start: Number(bookingWindowRange.booking_window_start),
            booking_window_end: Number(bookingWindowRange.booking_window_end),
            rules: audienceRules
              .filter(
                (rule) =>
                  rule.source ||
                  rule.operator ||
                  rule.daysRange?.days_from ||
                  rule.daysRange?.days_to,
              )
              .map((rule) => ({
                type: rule.type,
                source: rule.source || "",
                operator: rule.operator || "",
                days_from: Number(rule.daysRange?.days_from || 0),
                days_to: Number(rule.daysRange?.days_to || 0),
              })),
            shared_audience_ids: selectedObjective === "PROSPECTING" && selectedIncludeAudiences.length > 0
              ? selectedIncludeAudiences.map(Number)
              : undefined,
          };

      const payload = {
        campaign_id: Number(effectiveCampaignId),
        campaign_audience: audienceEntry,
      };

      await axios.put(
        `${APP_META_TRAVEL_ADS_API_ENDPOINT}/api/meta-ads/edit-campaign/audience/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      enqueueSnackbar("Audience settings saved successfully!", { variant: "success" });
      onClose();
    } catch (error) {
      console.error("Error saving audience settings:", error);
      const data = error?.response?.data;
      const audienceErrors = data?.campaign_audience;

      const messages = [];
      const collectMessages = (val, path = []) => {
        if (Array.isArray(val)) {
          val.forEach((item) => {
            if (item && typeof item === "object" && item.errorMessage) {
              messages.push(`${item.errorMessage}`);
            } else if (typeof item === "string") {
              messages.push(`${item}`);
            }
          });
          return;
        }
        if (val && typeof val === "object") {
          if(val.errorMessage) {
            messages.push(`${val.errorMessage}`);
          } else {
            Object.entries(val).forEach(([k, v]) => collectMessages(v, [...path, k]));
          }
          return;
        }
        if (typeof val === "string" && val.trim()) {
          messages.push(`${val}`);
        }
      };

      if (audienceErrors) collectMessages(audienceErrors, ["campaign_audience"]);
      if (Array.isArray(data?.campaign_id) && data.campaign_id.length) {
        collectMessages({ campaign_id: data.campaign_id }, []);
      }

      if (messages.length) {
        messages.forEach((m) => enqueueSnackbar(m, { variant: "error" }));
      } else {
        enqueueSnackbar(data?.errorMessage || data?.message || "Failed to save audience settings.", { variant: "error" });
      }
    } finally {
      setIsLoading(false);
      setDrawerOpen(false);
    }
  };

  return {
    saveAudience,
    isLoading
  };
};

export default useSaveAudience;